import axios from 'axios';
import { getKeycloakInstance } from './keycloak';
import { store } from '../store/store';
import { setAuthenticated, logout } from '../store/slices/authSlice';
import { parseKeycloakToken } from './parseKeycloakToken';
import { KeycloakParsedToken } from '../types/token';
import {resetInstances} from "../store/slices/instancesSlice";
import {resetImages} from "../store/slices/imagesSlice";
import {resetFlavors} from "../store/slices/flavorsSlice";
import {resetOverview} from "../store/slices/overviewSlice";

let isRefreshing = false;
let failedQueue: {
    resolve: (token: string) => void;
    reject: (err: any) => void;
}[] = [];

const processQueue = (error: any, token: string | null = null) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token as string);
        }
    });
    failedQueue = [];
};

const axiosInstance = axios.create({
    baseURL: process.env.REACT_APP_BASE_URL || '/',
});

axiosInstance.interceptors.request.use(async (config) => {
    const keycloak = getKeycloakInstance();
    const token = keycloak.token;

    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
});

axiosInstance.interceptors.response.use(
    response => response,
    async error => {
        const originalRequest = error.config;
        const keycloak = getKeycloakInstance();

        const status = error.response?.status;
        // A 403 carrying redu's own error envelope is an APPLICATION refusal (a capability gate, a
        // flavor that needs approval, a locked trial resource), not an expired token. Treating it as
        // an auth error sends the user through a token refresh and, when that fails, logs them out:
        // picking a size you are not approved for would end your session. Only a 403 with no
        // envelope is treated as auth, which is what a genuine Keycloak rejection looks like.
        const hasErrorEnvelope = !!error.response?.data?.error?.code;
        const isAuthError =
            (status === 401 || (status === 403 && !hasErrorEnvelope)) && !originalRequest._retry;

        if (isAuthError) {
            if (!keycloak) return Promise.reject(error);

            if (isRefreshing) {
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject });
                }).then(token => {
                    originalRequest.headers.Authorization = `Bearer ${token}`;
                    return axiosInstance(originalRequest);
                });
            }

            originalRequest._retry = true;
            isRefreshing = true;

            const maxRetries = 2;
            let retryCount = 0;

            while (retryCount <= maxRetries) {
                try {
                    const refreshed = await keycloak.updateToken(30);

                    if (!refreshed || !keycloak.token || !keycloak.tokenParsed) {
                        throw new Error('Token refresh failed');
                    }

                    const token = keycloak.token;
                    const userInfo = parseKeycloakToken(keycloak.tokenParsed as unknown as KeycloakParsedToken);

                    store.dispatch(setAuthenticated({
                        isAuthenticated: true,
                        token,
                        userInfo,
                    }));

                    processQueue(null, token);

                    originalRequest.headers.Authorization = `Bearer ${token}`;
                    return axiosInstance(originalRequest);
                } catch (refreshErr) {
                    retryCount++;
                    if (retryCount > maxRetries) {
                        processQueue(refreshErr, null);
                        store.dispatch(logout());
                        store.dispatch(resetInstances());
                        store.dispatch(resetImages());
                        store.dispatch(resetFlavors());
                        store.dispatch(resetOverview());
                        keycloak.logout();
                        return Promise.reject(refreshErr);
                    }
                } finally {
                    if (retryCount > maxRetries) {
                        isRefreshing = false;
                    }
                }
            }
        }

        return Promise.reject(error);
    }
);

export default axiosInstance;
